import React, { useState, useRef, useEffect } from "react";
import type { Clip, Track, Transform, Subtitle, SubtitleStyle, MontageVersion } from "../../types";
import { Play, Pause, Expand, Minimize, CaptionsOff, RectangleHorizontal, Square, RectangleVertical, ChevronDown, SkipBack, SkipForward, ArrowLeft, Save, Gauge, Volume2, VolumeX, Loader2, Flame } from "lucide-react";
import { formatDuration } from '../../utils';
import { AspectRatio } from "../../webcodecs-exporter";
import { PREVIEW_RESOLUTION } from './constants';
import { SeekBar } from './SeekBar';

interface PreviewAreaProps {
    // State
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    playbackSpeed: number;
    aspectRatio: AspectRatio;
    playerSize: string;
    activeSubtitleLanguage: string;
    subtitleStyle: SubtitleStyle;
    selectedClipId: string | null;
    scaleMode?: 'fit' | 'fill';
    canvasDimensions: { width: number, height: number, clipBounds: null | DOMRect, scale: number };
    isMuted?: boolean;
    isBuffering?: boolean;
    onToggleMute?: () => void;
    onTranslateSubtitles?: (targetLang: string) => void;
    onGenerateSubtitles?: (clip: Clip) => void;
    translationsInProgress?: Set<string>;

    // Montage / Navigation
    montageVersions: MontageVersion[];
    currentVersionId: string | null;
    canNavigateUp?: boolean;
    navigationBreadcrumbs?: string[];

    // Data
    allClips: Clip[];
    tracks: Track[];

    // Refs
    canvasRef: React.RefObject<HTMLCanvasElement>;
    canvasContainerRef: React.RefObject<HTMLDivElement>;

    // Editing State
    editingTextClipId: string | null;
    editingTextValue: string;
    editingFontFamily: string;
    availableFonts: { name: string, family: string, fontWeight?: number }[];

    // Actions
    onTogglePlay: () => void;
    onSkipToStart: () => void;
    onSkipToEnd: () => void;
    onSpeedChange: (speed: number) => void;
    onAspectRatioChange: (ratio: AspectRatio) => void;
    onPlayerSizeChange: (size: string) => void;
    onSelectVersion: (id: string) => void;
    onNavigateUp?: () => void;
    onActiveSubtitleLanguageChange: (lang: string) => void;
    onSave?: () => void;
    onSeek: (time: number) => void; // New Prop
    onSeekbarScrubStart?: () => void; // New Prop
    onSeekbarScrubEnd?: () => void;   // New Prop

    // Canvas Interactions
    onCanvasInteractionStart: (e: React.MouseEvent, action: string, handle?: string) => void;
    onCanvasDoubleClick: (e: React.MouseEvent) => void;
    onCanvasClick?: (e: React.MouseEvent) => void;
    onCanvasWheel: (e: React.WheelEvent) => void;
    onCanvasHover: (isHovering: boolean) => void;

    // Text Editing
    setEditingTextClipId: (id: string | null) => void;
    setEditingTextValue: (val: string) => void;
    setEditingFontFamily: (font: string) => void;
    handleTextEditComplete: () => void;

    // Translation (Optional: Keeping it here since it was in the top section)
    translationTargetLang: string | null;
    setTranslationTargetLang: (lang: string | null) => void;
    translationSourceLangs: Record<string, boolean>;
    setTranslationSourceLangs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    // Styles
    previewSectionHeight: number | null;
    // Loading state
    projectLoadingState?: { isLoading: boolean; thumbnailUrl?: string };
    onOpenExportMenu?: () => void;
}

export const PreviewArea: React.FC<PreviewAreaProps> = ({
    isPlaying, currentTime, duration, playbackSpeed, aspectRatio, playerSize,
    activeSubtitleLanguage, subtitleStyle, selectedClipId, scaleMode, canvasDimensions,
    montageVersions, currentVersionId, canNavigateUp, navigationBreadcrumbs,
    allClips, tracks,
    canvasRef, canvasContainerRef,
    editingTextClipId, editingTextValue, editingFontFamily, availableFonts,
    onTogglePlay, onSkipToStart, onSkipToEnd, onSpeedChange, onAspectRatioChange, onPlayerSizeChange, isMuted, isBuffering, onToggleMute,
    onTranslateSubtitles, onGenerateSubtitles, translationsInProgress,
    onSelectVersion,
    onNavigateUp,
    onActiveSubtitleLanguageChange,
    onSave,
    onSeek,
    onSeekbarScrubStart,
    onSeekbarScrubEnd,
    onCanvasInteractionStart, onCanvasDoubleClick, onCanvasWheel, onCanvasHover, onCanvasClick,
    setEditingTextClipId, setEditingTextValue, setEditingFontFamily, handleTextEditComplete,
    translationTargetLang, setTranslationTargetLang, translationSourceLangs, setTranslationSourceLangs,
    previewSectionHeight,
    projectLoadingState,
    onOpenExportMenu
}) => {
    const [isHoveringStage, setIsHoveringStage] = useState(true);
    const [isSubtitleMenuOpen, setIsSubtitleMenuOpen] = useState(false);
    const [isSpeedMenuOpen, setIsSpeedMenuOpen] = useState(false);

    // Helper to determine transform for handles
    const selectedClip = allClips.find(c => c.id === selectedClipId);
    const transformForHandles = selectedClip?.type === 'zoom' ? selectedClip.endTransform : selectedClip?.transform;

    return (
        <>
            {/* --- Translation Popup --- */}
            {translationTargetLang && (
                <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={() => {
                        setTranslationTargetLang(null);
                        // Reset temp selection state is handled by the component remounting or state logic below if we moved it out,
                        // but since it uses a parent state or prop, we might want to clear it? 
                        // Actually, better to just use local state for the selection inside a sub-component or 
                        // reset the parent state here. For now, we reuse translationSourceLangs as "selectedTargets".
                        setTranslationSourceLangs({});
                    }}
                >
                    <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 shadow-2xl w-80" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-white mb-4">Traduire les sous-titres</h3>

                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-2">Vers les langues :</label>
                            <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                                {['fr', 'en', 'es', 'de', 'it', 'pt'].map(lang => (
                                    <label key={lang} className="flex items-center gap-3 p-2 bg-black/20 rounded-lg hover:bg-black/40 cursor-pointer transition-colors">
                                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                            // Auto-select the target lang that opened the popup, OR any manually checked ones
                                            (lang === translationTargetLang || translationSourceLangs[lang])
                                                ? 'bg-blue-600 border-blue-600'
                                                : 'border-gray-600'
                                            } `}>
                                            {(lang === translationTargetLang || translationSourceLangs[lang]) && <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={lang === translationTargetLang || !!translationSourceLangs[lang]}
                                            onChange={() => {
                                                // logic: if it matches targetLang, we can't uncheck it? Or maybe we can?
                                                // Let's allow unchecking the primary one if we want, but usually we want it.
                                                // Actually simpler: treating translationTargetLang as just the "initial" one.
                                                // We sync it into translationSourceLangs on mount? 
                                                // For simplicity: We toggle it in translationSourceLangs. 
                                                // But translationTargetLang is a string (prop).
                                                // Let's simply toggle:
                                                if (lang === translationTargetLang) {
                                                    // If user clicks the one that opened the modal, maybe close modal or just unselect?
                                                    // Let's keep it simple: It's always selected visually if it matches target, 
                                                    // but to truly support "uncheck", we should probably use a local state array initialized with [targetLang].
                                                    // However, to avoid refactoring the whole parent state, we'll use the prop + the map.
                                                    // If user clicks the 'active' one, we do nothing or let them toggle 'extra' status?
                                                    // Let's make it fully controlled by the map effectively.
                                                }
                                                setTranslationSourceLangs(prev => ({ ...prev, [lang]: !prev[lang] }));
                                            }}
                                        />
                                        <span className="text-sm font-medium text-gray-200 uppercase">{lang}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setTranslationTargetLang(null);
                                    setTranslationSourceLangs({});
                                }}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => {
                                    if (onTranslateSubtitles) {
                                        // 1. Always translate the primary target
                                        // onTranslateSubtitles(translationTargetLang);

                                        // 2. Translate others
                                        // Merge primary + checked
                                        const allTargets = new Set<string>();
                                        allTargets.add(translationTargetLang);
                                        Object.entries(translationSourceLangs).forEach(([lang, selected]) => {
                                            if (selected) allTargets.add(lang);
                                        });

                                        // Execute
                                        allTargets.forEach(lang => {
                                            // Add small delay to avoid race conditions if any? 
                                            // The backend keeps track of jobs so it should be fine.
                                            // UI might show multiple toasts.
                                            setTimeout(() => onTranslateSubtitles(lang), 100);
                                        });
                                    }
                                    setTranslationTargetLang(null);
                                    setTranslationSourceLangs({});
                                }}
                                className="px-5 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                            >
                                Traduire
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- STAGE (VIDEO PREVIEW) --- */}
            <div
                id="preview-section"
                className="relative flex flex-col flex-shrink-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gray-800 via-[#0a0a0a] to-[#050505] z-10"
                style={{
                    height: playerSize === 'fullscreen' ? '100%' : (previewSectionHeight === null ? '60%' : `${previewSectionHeight}px`),
                }}
                onMouseEnter={() => setIsHoveringStage(true)}
                onMouseLeave={() => setIsHoveringStage(false)}
            >
                {/* Floating Top Navigation */}
                <div className={`absolute top-4 left-4 right-4 z-50 flex justify-between items-start pointer-events-none transition-all duration-500 ${!isPlaying || isHoveringStage ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'} `}>
                    <div className="flex gap-2 items-center pointer-events-auto" onMouseEnter={() => setIsHoveringStage(true)}>
                        {canNavigateUp && (
                            <button onClick={onNavigateUp} className="group flex items-center gap-2 bg-black/40 hover:bg-black/60 backdrop-blur-md text-white/90 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 border border-white/5">
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                <span>{navigationBreadcrumbs && navigationBreadcrumbs.length > 1 ? navigationBreadcrumbs[navigationBreadcrumbs.length - 2] : 'Back'}</span>
                            </button>
                        )}
                        {/* Save Button */}
                        {/* Save Button */}
                        <button
                            onClick={() => {
                                // Visual feedback
                                const btn = document.getElementById('save-project-btn');
                                if (btn) {
                                    const originalContent = btn.innerHTML;
                                    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg> Saved!';
                                    btn.classList.add('bg-green-500/80', 'text-white');
                                    btn.classList.remove('bg-white/10', 'hover:bg-white/20');
                                    setTimeout(() => {
                                        btn.innerHTML = originalContent;
                                        btn.classList.remove('bg-green-500/80', 'text-white');
                                        btn.classList.add('bg-white/10', 'hover:bg-white/20');
                                    }, 2000);
                                }
                                // Actual Save
                                if (onSave) onSave();
                            }}
                            id="save-project-btn"
                            className="h-9 px-4 rounded-full flex items-center gap-2 text-sm font-medium bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/5 transition-all active:scale-95"
                        >
                            <Save className="w-4 h-4" /> Save
                        </button>
                        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md border border-white/5 rounded-full px-4 py-2 shadow-lg">
                            <select
                                value={currentVersionId || ''}
                                onChange={(e) => e.target.value && onSelectVersion(e.target.value)}
                                disabled={montageVersions.length === 0}
                                className="bg-transparent text-white text-sm font-semibold focus:outline-none cursor-pointer hover:text-blue-300 transition-colors"
                                style={{ appearance: 'none' }} // Hide default arrow
                            >
                                {montageVersions.length === 0 && <option value="">Version unique</option>}
                                {montageVersions.map(version => (
                                    <option key={version.id} value={version.id} className="text-black">{version.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pointer-events-auto" onMouseEnter={() => setIsHoveringStage(true)}>
                        {/* Aspect Ratio Switcher */}
                        <div className="flex items-center p-1 bg-black/40 backdrop-blur-md border border-white/5 rounded-full">
                            {[
                                { ratio: '16:9', Icon: RectangleHorizontal },
                                { ratio: '1:1', Icon: Square },
                                { ratio: '9:16', Icon: RectangleVertical }
                            ].map(({ ratio: r, Icon }) => (
                                <button
                                    key={r}
                                    onClick={() => onAspectRatioChange(r as AspectRatio)}
                                    className={`p-1.5 rounded-full transition-all ${aspectRatio === r ? 'bg-white/20 text-white shadow-sm' : 'text-white/40 hover:text-white hover:bg-white/5'} `}
                                    title={r}
                                >
                                    <Icon className={r === '9:16' ? "w-3 h-4" : r === '16:9' ? "w-4 h-3" : "w-3.5 h-3.5"} />
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={onToggleMute}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/5 text-white/70 hover:text-white transition-all"
                            title={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>

                        <button
                            onClick={() => onPlayerSizeChange(playerSize === 'fullscreen' ? 'normal' : 'fullscreen')}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/5 text-white/70 hover:text-white transition-all"
                            title="Fullscreen"
                        >
                            <Expand className="w-4 h-4" />
                        </button>
                    </div>
                </div>


                {/* Buffering Indicator */}
                {isBuffering && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/20 pointer-events-none fade-in duration-300">
                        <div className="bg-black/40 backdrop-blur-md p-4 rounded-full border border-white/10 shadow-2xl animate-in zoom-in duration-300">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                    </div>
                )}

                {/* Main Canvas Area */}
                <div
                    className="relative w-full h-full flex items-center justify-center overflow-hidden"
                    onMouseEnter={() => setIsHoveringStage(true)}
                    onMouseLeave={() => setIsHoveringStage(false)}
                >
                    <div ref={canvasContainerRef} className="absolute inset-0 shadow-2xl">
                        <canvas
                            ref={canvasRef}
                            width={PREVIEW_RESOLUTION.width}
                            height={PREVIEW_RESOLUTION.height}
                            className="w-full h-full block object-contain drop-shadow-2xl"
                            onMouseDown={(e) => onCanvasInteractionStart(e, 'move')}
                            onDoubleClick={onCanvasDoubleClick}
                            onMouseEnter={() => onCanvasHover(true)}
                            onMouseLeave={() => onCanvasHover(false)}
                            onWheel={onCanvasWheel}
                            onClick={onCanvasClick}
                        />

                        {/* Project Loading Transition Overlay - On Canvas */}
                        {projectLoadingState?.isLoading && (
                            <div className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center gap-6 transition-opacity duration-300">
                                {projectLoadingState.thumbnailUrl && (
                                    <img
                                        src={projectLoadingState.thumbnailUrl}
                                        alt="Loading project"
                                        className="w-48 h-auto max-h-32 object-cover rounded-lg shadow-2xl opacity-80"
                                    />
                                )}
                                <div className="flex items-center gap-3">
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    <span className="text-white/70 text-sm font-medium">Chargement...</span>
                                </div>
                            </div>
                        )}

                        {/* Transform Handles Overlay */}
                        {selectedClipId && canvasDimensions.clipBounds && transformForHandles && (() => {
                            // FIX: Cast ref to `any` to access `getBoundingClientRect`.
                            const containerRect = canvasContainerRef.current?.getBoundingClientRect();
                            if (!containerRect) return null;

                            const centerX = (canvasDimensions.clipBounds.x - containerRect.x) + canvasDimensions.clipBounds.width / 2;
                            const centerY = (canvasDimensions.clipBounds.y - containerRect.y) + canvasDimensions.clipBounds.height / 2;

                            const handleOuterMargin = 20;
                            const halfHeight = canvasDimensions.clipBounds.height / 2;
                            const halfWidth = canvasDimensions.clipBounds.width / 2;
                            const handleYRelativeToCenter = -(halfHeight + handleOuterMargin);

                            return (
                                <>
                                    {/* Rotation handle */}
                                    <div
                                        className="absolute pointer-events-none z-30"
                                        style={{ left: `${centerX}px`, top: `${centerY}px`, transform: `rotate(${transformForHandles.rotation}deg)`, transformOrigin: `0 0` }} >
                                        <div className="absolute" style={{ left: '-1px', top: '0px', width: '0px', height: `${handleYRelativeToCenter}px`, borderLeft: '2px dotted #10b981' }} />
                                        <div
                                            className="absolute w-6 h-6 bg-[#10b981] border-2 border-white rounded-full cursor-grab active:cursor-grabbing pointer-events-auto shadow-[0_0_15px_rgba(16,185,129,0.5)] hover:scale-110 transition-transform"
                                            style={{ left: `-12px`, top: `${handleYRelativeToCenter - 12}px` }}
                                            onMouseDown={(e) => onCanvasInteractionStart(e, 'rotate')}
                                            title="Rotate"
                                        >
                                            <svg className="w-full h-full p-1" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                <path d="M21.5 2v6h-6M21.5 2L15 8.5M2.5 22v-6h6M2.5 22L9 15.5" />
                                            </svg>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {/* Text Editor Overlay */}
                        {editingTextClipId && (() => {
                            const editingClip = allClips.find(c => c.id === editingTextClipId);
                            if (!editingClip) return null;

                            return (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-40" onClick={handleTextEditComplete}>
                                    <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 shadow-2xl w-96 transform scale-100 transition-all" onClick={(e) => e.stopPropagation()}>
                                        <div className="mb-4">
                                            <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Typography</label>
                                            <div className="relative">
                                                <select
                                                    value={editingFontFamily}
                                                    onChange={(e) => setEditingFontFamily(e.target.value)}
                                                    className="w-full appearance-none bg-black/30 text-white border border-white/10 rounded-lg p-3 pr-8 focus:outline-none focus:border-blue-500 font-medium"
                                                >
                                                    {availableFonts.map((font) => (
                                                        <option key={font.family} value={font.family} style={{ fontFamily: font.family }}>{font.name}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                                            </div>
                                        </div>
                                        <textarea
                                            autoFocus
                                            value={editingTextValue}
                                            onChange={(e) => setEditingTextValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTextEditComplete(); }
                                                else if (e.key === 'Escape') { setEditingTextClipId(null); setEditingTextValue(''); }
                                            }}
                                            className="w-full h-32 bg-black/30 text-white border border-white/10 rounded-lg p-3 resize-none focus:outline-none focus:border-blue-500 placeholder-white/20"
                                            style={{ fontFamily: editingFontFamily, fontSize: '16px', textAlign: editingClip.style?.textAlign || 'center' }}
                                            placeholder="Type your text..."
                                        />
                                        <div className="flex justify-end gap-3 mt-4">
                                            <button onClick={() => { setEditingTextClipId(null); setEditingTextValue(''); }} className="px-4 py-2 hover:bg-white/5 rounded-lg text-sm text-white/60 transition-colors">Cancel</button>
                                            <button onClick={handleTextEditComplete} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg shadow-lg shadow-blue-900/20 transition-all hover:scale-105 active:scale-95">Save Changes</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                <div className="w-full flex items-center justify-between px-4 py-2 bg-[#09090b] border-t border-[rgba(255,255,255,0.05)] z-40 shrink-0 select-none">
                    {/* Time Display Left */}
                    <div className="flex-1 flex items-center gap-3">
                        <span className="font-mono text-sm font-medium text-white/80">{formatDuration(currentTime)}</span>
                        <span className="font-mono text-xs text-white/30 hidden sm:inline">/ {formatDuration(duration)}</span>
                    </div>

                    {/* Transport Controls (Center) */}
                    <div className="flex-1 flex items-center justify-center gap-4">
                        <button onClick={onSkipToStart} className="p-2 text-white/50 hover:text-white transition-colors" title="Start">
                            <SkipBack className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onTogglePlay}
                            className="w-10 h-10 flex items-center justify-center bg-white text-black rounded-full hover:scale-105 active:scale-95 transition-all shadow-[0_0_15px_rgba(255,255,255,0.15)]"
                            title={isPlaying ? "Pause (Space)" : "Play (Space)"}
                        >
                            {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
                        </button>
                        <button onClick={onSkipToEnd} className="p-2 text-white/50 hover:text-white transition-colors" title="End">
                            <SkipForward className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Right controls */}
                    <div className="flex-1 flex items-center justify-end gap-3 pr-2">
                        {/* Subtitles */}
                        <div className="relative">
                            <button
                                onClick={() => setIsSubtitleMenuOpen(!isSubtitleMenuOpen)}
                                className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${activeSubtitleLanguage !== 'none' ? 'text-blue-400' : 'text-white/50 hover:text-white'}`}
                                title="Subtitles"
                            >
                                <CaptionsOff className="w-4 h-4" />
                                <span className="uppercase">{activeSubtitleLanguage === 'none' ? 'Off' : activeSubtitleLanguage}</span>
                            </button>
                            {isSubtitleMenuOpen && (() => {
                                const LANG_LABELS: Record<string, string> = { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', it: 'Italiano', pt: 'Português', ar: 'العربية', zh: 'Chinese', ja: 'Japanese', ko: 'Korean', ru: 'Русский', nl: 'Nederlands', tr: 'Türkçe', pl: 'Polski', sv: 'Svenska', hi: 'हिन्दी' };
                                const ALL_TRANSLATE_LANGS = ['fr', 'en', 'es', 'de', 'it', 'pt', 'ar', 'zh', 'ja', 'ko', 'ru', 'nl', 'tr'];
                                // Collect all languages that have actual subtitles across all clips
                                const availableLangs = new Set<string>();
                                allClips.forEach(c => {
                                    if (c.subtitles) Object.keys(c.subtitles).forEach(lang => {
                                        if (c.subtitles![lang] && c.subtitles![lang].length > 0) availableLangs.add(lang);
                                    });
                                });
                                const sortedAvailable = Array.from(availableLangs).sort();
                                const translatableLangs = ALL_TRANSLATE_LANGS.filter(l => !availableLangs.has(l));
                                const hasAnySubtitles = availableLangs.size > 0;

                                return (
                                    <div className="absolute bottom-full right-0 mb-3 bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[180px] flex flex-col z-50">
                                        {/* Off button */}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onActiveSubtitleLanguageChange('none'); setIsSubtitleMenuOpen(false); }}
                                            className={`w-full px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${activeSubtitleLanguage === 'none' ? 'text-white font-bold bg-white/5' : 'text-gray-400 hover:bg-white/5'}`}
                                        >
                                            <CaptionsOff className="w-3.5 h-3.5" />
                                            <span>Off</span>
                                        </button>

                                        {/* Available languages */}
                                        {sortedAvailable.length > 0 && (
                                            <>
                                                <div className="h-px bg-white/5 mx-2" />
                                                <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-white/30 tracking-wider">Disponibles</div>
                                                {sortedAvailable.map(lang => (
                                                    <button
                                                        key={lang}
                                                        onClick={(e) => { e.stopPropagation(); onActiveSubtitleLanguageChange(lang); setIsSubtitleMenuOpen(false); }}
                                                        className={`w-full px-4 py-2 text-sm transition-colors flex items-center justify-between ${activeSubtitleLanguage === lang ? 'text-blue-400 font-bold bg-blue-500/10' : 'text-gray-200 hover:bg-white/5'}`}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <span className="w-5 text-center font-mono text-xs uppercase">{lang}</span>
                                                            <span className="text-white/50 text-xs">{LANG_LABELS[lang] || lang}</span>
                                                        </span>
                                                        {activeSubtitleLanguage === lang && (
                                                            <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                                        )}
                                                    </button>
                                                ))}
                                            </>
                                        )}

                                        {/* Translatable languages */}
                                        {hasAnySubtitles && translatableLangs.length > 0 && (
                                            <>
                                                <div className="h-px bg-white/5 mx-2 mt-1" />
                                                <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-white/30 tracking-wider">Traduire vers</div>
                                                {translatableLangs.map(lang => (
                                                    <button
                                                        key={lang}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (setTranslationTargetLang) setTranslationTargetLang(lang);
                                                            setIsSubtitleMenuOpen(false);
                                                        }}
                                                        className="w-full px-4 py-2 text-sm transition-colors flex items-center justify-between text-gray-400 hover:bg-white/5 hover:text-gray-200"
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <span className="w-5 text-center font-mono text-xs uppercase">{lang}</span>
                                                            <span className="text-white/30 text-xs">{LANG_LABELS[lang] || lang}</span>
                                                        </span>
                                                        <span className="text-[10px] bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded font-bold">
                                                            {translationsInProgress?.has(lang) ? <Loader2 className="w-3 h-3 animate-spin" /> : '+ ADD'}
                                                        </span>
                                                    </button>
                                                ))}
                                            </>
                                        )}

                                        {/* If no subtitles at all, show generate button */}
                                        {!hasAnySubtitles && (
                                            <>
                                                <div className="h-px bg-white/5 mx-2" />
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const primaryClip = selectedClipId ? allClips.find(c => c.id === selectedClipId) : allClips.find(c => c.type === 'video');
                                                        if (primaryClip && onGenerateSubtitles) onGenerateSubtitles(primaryClip);
                                                        setIsSubtitleMenuOpen(false);
                                                    }}
                                                    className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 transition-colors flex items-center gap-2 font-medium"
                                                >
                                                    {allClips.some(c => c.isProcessingSubtitles)
                                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcription en cours...</>
                                                        : <><Flame className="w-3.5 h-3.5" /> Générer les sous-titres</>}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="h-4 w-px bg-white/10 mx-1" />

                        {/* Speed */}
                        <div className="relative group/speed">
                            <button
                                onClick={() => setIsSpeedMenuOpen(!isSpeedMenuOpen)}
                                className="flex items-center gap-1.5 text-sm font-medium text-white/70 hover:text-white transition-colors"
                            >
                                <Gauge className="w-4 h-4" />
                                <span>{playbackSpeed}x</span>
                            </button>
                            {isSpeedMenuOpen && (
                                <div className="absolute bottom-full right-0 mb-3 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-xl overflow-hidden py-1 min-w-[80px] flex flex-col z-50">
                                    {[1, 1.2, 1.5, 2].map(speed => (
                                        <button
                                            key={speed}
                                            onClick={(e) => { e.stopPropagation(); onSpeedChange(speed); setIsSpeedMenuOpen(false); }}
                                            className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors ${playbackSpeed === speed ? 'text-blue-400 font-bold bg-white/5' : 'text-gray-300'} `}
                                        >
                                            {speed}x
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* SEEK BAR (Inserted at bottom of PreviewArea but above the timeline boundary) */}
                <div className="relative w-full h-[6px] shrink-0 bg-black group z-50">
                    <SeekBar
                        currentTime={currentTime}
                        duration={duration}
                        onSeek={onSeek}
                        onScrubStart={onSeekbarScrubStart}
                        onScrubEnd={onSeekbarScrubEnd}
                    />
                </div>
            </div >
        </>
    );
};

